import { useEffect, useMemo, useState } from "react";
import { IconRefresh } from "@tabler/icons-react";
import { BotonExportarExcel, IconAction, PageHeader, PaginadorTabla, Paper, Skeleton, Stack, Text } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { ColumnaExcel } from "@/shared/export/exportar-excel";
import { useMisFacturas } from "../hooks/use-mis-facturas";
import { listarMisFacturas, type Factura } from "../services/facturas.api";
import { aParamsServer, type FacturaFiltros, type FacturaSort } from "../utils/ordenar-facturas";
import { FacturasTable } from "./FacturasTable";

const COLUMNAS_EXCEL: ColumnaExcel<Factura>[] = [
  { header: "UUID", value: (f) => f.uuid.toUpperCase() },
  { header: "Emisor", value: (f) => f.nombreEmisor },
  { header: "RFC emisor", value: (f) => f.rfcEmisor },
  { header: "Receptor", value: (f) => f.nombreReceptor },
  { header: "RFC receptor", value: (f) => f.rfcReceptor },
  { header: "Fecha validación", value: (f) => f.fechaValidacion },
  { header: "Fecha timbrado", value: (f) => f.fechaTimbrado },
  { header: "Fecha emisión", value: (f) => f.fechaEmision },
  { header: "Total", value: (f) => f.total },
  { header: "Moneda", value: (f) => f.moneda },
  { header: "Estatus", value: (f) => f.estatus },
  { header: "Serie", value: (f) => f.serie },
  { header: "Folio", value: (f) => f.folio },
  { header: "Orden de compra", value: (f) => f.ordenCompraClave },
  { header: "Tipo", value: (f) => f.tipoComprobante },
  { header: "Observaciones", value: (f) => f.observaciones },
  { header: "Núm. documento", value: (f) => f.numeroSolicitud },
];

const PAGE_SIZE = 15;

/**
 * "Mis facturas" (P30) · los acuses de validación de los CFDIs cargados por el
 * solicitante actual, con estatus Válido/No válido/Cancelado/En proceso y
 * descarga del XML original + acuse en PDF. Filtro por columna (fila de
 * búsqueda bajo los encabezados · mismo patrón que Mis solicitudes), orden por
 * columna y PAGINADO — todo resuelto en el SERVIDOR (con debounce); el filtro
 * también se aplica en cliente sobre la página visible para feedback
 * instantáneo mientras llega la respuesta.
 */
export function MisFacturasPage() {
  const [filtros, setFiltros] = useState<FacturaFiltros>({});
  const [sort, setSort] = useState<FacturaSort | null>(null);
  const [page, setPage] = useState(1);
  const [filtrosDebounced] = useDebouncedValue(filtros, 350);

  const serverParams = useMemo(
    () => ({ ...aParamsServer(filtrosDebounced, sort), page, pageSize: PAGE_SIZE }),
    [filtrosDebounced, sort, page],
  );

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtrosDebounced]);

  const { data, isPending, isError, error, refetch, isFetching, isPlaceholderData } =
    useMisFacturas(serverParams);
  const facturas = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title="Mis facturas"
        description="CFDIs validados en tus solicitudes de pago · cada carga genera un acuse de validación descargable en PDF."
        actions={
          <>
            <BotonExportarExcel
              archivo="mis-facturas"
              hoja="Facturas"
              columnas={COLUMNAS_EXCEL}
              obtenerFilas={async () => {
                // Todas las páginas con los filtros/orden actuales.
                const params = aParamsServer(filtrosDebounced, sort);
                const todas: Factura[] = [];
                for (let p = 1; ; p++) {
                  const pagina = await listarMisFacturas({ ...params, page: p, pageSize: 100 });
                  todas.push(...pagina.items);
                  if (todas.length >= pagina.total || pagina.items.length === 0) break;
                }
                return todas;
              }}
            />
            <IconAction label="Refrescar" variant="default" size="lg" onClick={() => refetch()} loading={isFetching}>
              <IconRefresh size={16} />
            </IconAction>
          </>
        }
      />
      <Stack gap="md">
        {isPending ? (
          <Paper padding="lg">
            <Stack gap="sm">
              <Skeleton height={36} />
              <Skeleton height={36} />
              <Skeleton height={36} />
            </Stack>
          </Paper>
        ) : isError ? (
          <Paper padding="lg">
            <Text c="peligro-600" size="sm">
              Error: {(error as Error).message}
            </Text>
          </Paper>
        ) : (
          <>
            <PaginadorTabla
              total={total}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              variante="superior"
            />
            <FacturasTable
              facturas={facturas}
              filtros={filtros}
              sort={sort}
              buscando={isPlaceholderData}
              onFiltrar={(key, valor) => setFiltros((prev) => ({ ...prev, [key]: valor }))}
              onOrdenar={setSort}
            />
            <PaginadorTabla
              total={total}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              sustantivo="facturas"
            />
            <Text size="xs" c="dimmed">
              El estatus "En proceso" se resuelve cuando el SAT confirma la vigencia del
              comprobante.
            </Text>
          </>
        )}
      </Stack>
    </div>
  );
}
